import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage } from '../i18n/LanguageContext';
import { localizeTour } from '../lib/tourI18n';
import { toast } from 'sonner';
import { 
  Clock, 
  Users, 
  Calendar,
  CreditCard,
  ChevronLeft,
  Check,
  Loader2,
  ShieldCheck,
  MapPin,
  Plus,
  Minus,
  UserPlus
} from 'lucide-react';

const BookingPage = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    date: '',
    time_slot: '',         // Optional start time (only used when tour exposes time_slots)
    passengers: 1,
    machines: 1,           // ATV/Buggy: number of machines
    second_passengers: 0,  // ATV/Buggy: number of 2nd passengers (each +fee)
    machine_flags: [0],     // ATV/Buggy: per-machine extras count (0..maxExtrasPerMachine)
    notes: '',
    add_ons: []
  });

  const [booking, setBooking] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState({});

  useEffect(() => {
    const fetchTour = async () => {
      try {
        const data = await api.getTour(tourId);
        setTour(data);
        // Flat-rate combos: default machines = 1, passengers = 1 × pax_per_machine.
        const FLAT_PAX_PER_MACHINE = {
          'tour-combo-gimme-all-double': 2,
          'tour-combo-gimme-all-atv-double': 2,
          'tour-combo-gimme-all-buggy-3pax': 3,
          'tour-combo-gimme-all-buggy-4pax': 4,
        };
        const ppm = FLAT_PAX_PER_MACHINE[data?.id];
        const minPax = data?.min_passengers || 1;
        const initialPax = ppm || minPax;
        if (ppm) {
          setFormData(prev => ({ ...prev, passengers: ppm, machines: 1 }));
        } else if (minPax > 1) {
          setFormData(prev => ({ ...prev, passengers: minPax }));
        }
        // Pre-select the optional lunch add-on for every passenger (low-friction upsell).
        // Clients can lower the qty to 0 if they don't want it.
        const addons = Array.isArray(data?.add_ons) ? data.add_ons : [];
        const lunch = addons.find(a => a?.id === 'typical-lunch');
        if (lunch) {
          setSelectedAddOns(prev => ({ ...prev, 'typical-lunch': initialPax }));
        }
      } catch (error) {
        console.error('Error fetching tour:', error);
        toast.error(t('booking.tourNotFound'));
        navigate('/tours');
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [tourId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'passengers' ? parseInt(value) : value
    }));
  };

  const EXTRA_PASSENGER_FEE = 30;
  const EXTRA_PASSENGER_FEE_TRANSFER = 20; // Transfer tours have $20 extra pax
  const EXTRA_PASSENGER_FEE_KAYAK = 20; // Kayak tour has different rate
  const BASE_PASSENGERS = 5;
  const MAX_PASSENGERS = 8;
  const VAT_RATE = 0.13; // 13% VAT
  
  // Transfer tours (different extra passenger fee)
  const TRANSFER_TOURS = [
    "tour-airport-lossuenos",
    "tour-arenal-transport", 
    "tour-luxury-lossuenos"
  ];
  const isTransfer = tour?.category === 'transfer';
  
  // Flat price tours (no extra pax option) — the package itself has a fixed price.
  const FLAT_PRICE_TOURS = [
    "tour-luxury-lossuenos",
    "tour-arenal-transport",
    "tour-combo-gimme-all-double",          // $270 NET + 13% VAT (1 double buggy / 2 pax, capacity=2)
    "tour-combo-gimme-all-atv-double",      // $210 NET + 13% VAT (1 ATV / 2 pax)
    "tour-combo-gimme-all-buggy-3pax",      // $365 NET + 13% VAT (1 buggy / 3 pax)
    "tour-combo-gimme-all-buggy-4pax"       // $460 NET + 13% VAT (1 buggy / 4 pax)
  ];
  const isFlatPrice = FLAT_PRICE_TOURS.includes(tourId);
  const GIMME_FLAT_VAT_IDS = [
    "tour-combo-gimme-all-double",
    "tour-combo-gimme-all-atv-double",
    "tour-combo-gimme-all-buggy-3pax",
    "tour-combo-gimme-all-buggy-4pax"
  ];
  const isGimmeFlatVat = GIMME_FLAT_VAT_IDS.includes(tourId);

  // Check if this is a special pricing tour (Manuel Antonio or Kayak Damas)
  const isManuelAntonio = tourId === 'tour-combo-manuel-antonio';
  const isKayakDamas = tourId === 'tour-combo-kayak-damas';
  const isBijagual = tourId === 'tour-bijagual-waterfall';
  const BIJAGUAL_EXTRA_PAX = 10;
  const BIJAGUAL_PARK_ENTRY = 20;
  // Buggies are priced PER BUGGY (each buggy seats up to 2 pax)
  const BUGGY_IDS = ["tour-buggy-2hrs", "tour-buggy-3hrs", "tour-buggy-4hrs"];
  const isBuggy = BUGGY_IDS.includes(tourId);
  const ATV_IDS = ["tour-atv-2hrs", "tour-atv-3hrs", "tour-atv-4hrs"];
  const isAtv = ATV_IDS.includes(tourId);
  // Manuel Antonio = flat transport (up to 5) + $30/extra + per-person add-on
  const isFlatCombo = isManuelAntonio;
  const KAYAK_PER_PERSON = 55;
  const isRaftingTour = tour?.category === 'rafting';
  // ALL prices on the catalog are now NET. 13% VAT is added at checkout for every booking.
  const isVatIncluded = false;
  // ATV/Buggy: NET base price per machine + extra fee per extra passenger in that machine.
  // ATV: 1 driver base + up to 1 optional extra passenger (+$30). Max 2 pax/ATV.
  // Buggy: 2 pax base + up to 2 optional extra passengers (+$40 each). Max 4 pax/buggy.
  const getMachineExtraFee = () => (isBuggy ? 40 : 30);
  const getMachineBaseSeats = () => (isBuggy ? 2 : 1);
  const getMaxExtrasPerMachine = () => (isBuggy ? 2 : 1);
  const getMaxMachines = () => {
    const maxPaxLocal = tour?.max_passengers || 8;
    const perMachine = getMachineBaseSeats() + getMaxExtrasPerMachine();
    return Math.max(1, Math.floor(maxPaxLocal / perMachine));
  };
  const getMachineCount = () => Math.max(1, parseInt(formData.machines) || 1);
  const getSecondPaxCount = () => {
    const m = getMachineCount();
    return Math.max(0, Math.min(parseInt(formData.second_passengers) || 0, m * getMaxExtrasPerMachine()));
  };
  const getActivityExtraCount = () => getSecondPaxCount();
  const getActivityExtraFee = () => getMachineExtraFee();
  const getActivityExtraTotal = () => getActivityExtraCount() * getActivityExtraFee();
  // Total people in the booking = machines × base_seats + extras
  const getMachineTotalPax = () => getMachineCount() * getMachineBaseSeats() + getSecondPaxCount();
  // Per-person pricing: non-transfer activities that are NOT ATV/buggy/flat-combo/kayak/GIMME-flat/Bijagual.
  const isPerPerson = !!tour && tour.category !== 'transfer' && !isBuggy && !isAtv && !isFlatCombo && !isKayakDamas && !isGimmeFlatVat && !isBijagual;
  const hasAddOns = tour?.add_ons && tour.add_ons.length > 0;
  const isRafting = tour?.category === 'rafting';
  const tl = localizeTour(tour, language);
  // Rafting has no fixed maximum — allow larger groups in the selector
  const maxPax = isRafting ? 16 : (tour?.max_passengers || MAX_PASSENGERS);
  
  const isManuelAntonioTransfer = tourId === 'tour-manuel-antonio-transfer';

  // Extra passenger fee depends on tour type
  const getExtraPassengerFee = () => {
    if (isKayakDamas) return EXTRA_PASSENGER_FEE_KAYAK;
    if (isManuelAntonioTransfer) return 30;  // SJO ↔ Manuel Antonio: $30/extra pax
    if (isTransfer) return EXTRA_PASSENGER_FEE_TRANSFER;
    if (isBijagual) return BIJAGUAL_EXTRA_PAX;
    return EXTRA_PASSENGER_FEE;
  };

  const getExtraPassengers = () => {
    return Math.max(0, formData.passengers - BASE_PASSENGERS);
  };

  const getExtraFee = () => {
    return getExtraPassengers() * getExtraPassengerFee();
  };

  // Calculate add-ons total
  const getAddOnsTotal = () => {
    if (!hasAddOns) return 0;
    let total = 0;
    Object.entries(selectedAddOns).forEach(([addonId, quantity]) => {
      if (quantity > 0) {
        const addon = tour.add_ons.find(a => a.id === addonId);
        if (addon) {
          total += addon.price_per_person * quantity;
        }
      }
    });
    return total;
  };

  // Extra VAT applied ONLY to vat_applicable add-ons when the parent tour is
  // VAT-included (so the add-on still carries its own 13% VAT at checkout).
  const getAddonVatExtra = () => {
    if (!hasAddOns || !tour) return 0;
    if (!isVatIncluded) return 0; // VAT will be applied to subtotal+addons below
    let extra = 0;
    Object.entries(selectedAddOns).forEach(([addonId, quantity]) => {
      if (quantity > 0) {
        const addon = tour.add_ons.find(a => a.id === addonId);
        if (addon && addon.vat_applicable) {
          extra += addon.price_per_person * quantity * VAT_RATE;
        }
      }
    });
    return Math.round(extra * 100) / 100;
  };

  const discountPercent = tour?.discount_percent || 0;

  const getDiscountableBase = () => {
    if (!tour) return 0;
    if (isGimmeFlatVat) return tour.price * Math.max(1, formData.machines || 1);  // flat × # machines
    if (isAtv || isBuggy) return tour.price * getMachineCount() + getActivityExtraTotal();
    if (isKayakDamas) return tour.price + getExtraFee() + KAYAK_PER_PERSON * formData.passengers;
    if (isBijagual) {
      // Transport $160 (up to 5) + $10/extra pax after 5 + $20/person + 13% VAT
      const extraPax = Math.max(0, formData.passengers - 5);
      return tour.price + extraPax * BIJAGUAL_EXTRA_PAX + BIJAGUAL_PARK_ENTRY * formData.passengers;
    }
    if (isPerPerson) return tour.price * formData.passengers;
    return tour.price + getExtraFee();
  };

  const getDiscountAmount = () => {
    if (!discountPercent) return 0;
    return Math.round(getDiscountableBase() * discountPercent) / 100;
  };

  const getSubtotal = () => {
    if (!tour) return 0;
    return getDiscountableBase() - getDiscountAmount() + getAddOnsTotal();
  };

  const getVatAmount = () => {
    if (isVatIncluded) return 0; // VAT already included in price
    return Math.round(getSubtotal() * VAT_RATE * 100) / 100;
  };

  const getTotalPrice = () => {
    if (isVatIncluded) {
      // Tour base already includes VAT; add only the VAT extra carried by vat_applicable add-ons (if any).
      return Math.round((getSubtotal() + getAddonVatExtra()) * 100) / 100;
    }
    return Math.round((getSubtotal() + getVatAmount()) * 100) / 100;
  };

  // Handle add-on quantity change
  const handleAddOnChange = (addonId, quantity) => {
    setSelectedAddOns(prev => ({
      ...prev,
      [addonId]: Math.max(0, quantity)
    }));
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    // Validate time slot when the tour exposes a schedule
    if (Array.isArray(tour?.time_slots) && tour.time_slots.length > 0 && !formData.time_slot) {
      toast.error(language === 'es' ? 'Por favor selecciona una hora de inicio' : 'Please pick a start time');
      return;
    }
    setSubmitting(true);
    
    try {
      // Prepare add-ons for backend
      const addOnsArray = Object.entries(selectedAddOns)
        .filter(([_, quantity]) => quantity > 0)
        .map(([id, quantity]) => ({ id, quantity }));
      
      // For ATV/Buggy, total pax = machines × base_seats + extras
      const computedPax = (isAtv || isBuggy)
        ? getMachineTotalPax()
        : formData.passengers;

      const bookingData = {
        tour_id: tourId,
        ...formData,
        passengers: computedPax,
        machines: (isAtv || isBuggy) ? getMachineCount() : (isGimmeFlatVat ? (formData.machines || 1) : undefined),
        second_passengers: (isAtv || isBuggy) ? getSecondPaxCount() : undefined,
        time_slot: formData.time_slot || undefined,
        add_ons: addOnsArray.length > 0 ? addOnsArray : undefined
      };
      const result = await api.createBooking(bookingData);
      setBooking(result);
      setStep(2);
      toast.success(t('booking.bookingCreated'));
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(t('booking.bookingFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async (method) => {
    if (!booking) return;
    setSubmitting(true);
    
    // Google Ads tracking - Begin Checkout event
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        'value': booking.total_price,
        'currency': 'USD'
      });
    }
    
    try {
      if (method === 'stripe') {
        const result = await api.createStripeCheckout(booking.id, 'stripe');
        window.location.href = result.checkout_url;
      } else if (method === 'paypal') {
        // PayPal.me payment - redirect directly to PayPal with amount
        const formattedTotal = Number(getTotalPrice()).toFixed(2);
        const paypalLink = `https://paypal.me/ErickCamachoMiranda/${formattedTotal}USD`;
        window.open(paypalLink, '_blank');
        toast.success(t('booking.paypalRedirect'));
        // Open email client for confirmation
        const subject = `PayPal Payment Confirmation - ${tour.title}`;
        const body = `Hello!

I just paid via PayPal for my booking:

Booking ID: ${booking.id}
Tour: ${tour.title}
Date: ${formData.date}${formData.time_slot ? `\nStart time: ${formData.time_slot}` : ''}
Passengers: ${formData.passengers}
Total: $${formattedTotal}

Name: ${formData.customer_name}
Email: ${formData.customer_email}
Phone: ${formData.customer_phone}

Thank you!`;
        setTimeout(() => {
          window.location.href = `mailto:reservations@travelguide.cr?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        }, 2000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(t('booking.paymentFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#1D4E41] animate-spin" />
      </div>
    );
  }

  if (!tour) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <WhatsAppButton />

      <section className="pt-28 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Back Button */}
          <button
            onClick={() => navigate('/tours')}
            data-testid="back-to-tours"
            className="flex items-center gap-2 text-gray-600 hover:text-[#1D4E41] mb-8 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            {t('booking.backToTours')}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tour Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] sticky top-28">
                <img
                  src={tour.image_url}
                  alt={tl.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <span className="px-3 py-1 bg-[#E8F5E9] text-[#1D4E41] rounded-full text-xs font-medium capitalize">
                    {tour.category}
                  </span>
                  <h2 className="text-xl font-semibold text-[#1A1A1A] mt-3 mb-2">{tl.title}</h2>
                  <p className="text-gray-600 text-sm mb-4 whitespace-pre-line">{tl.description}</p>

                  {tour.category === 'rafting' && (
                    <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="rafting-transport-note">
                      <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 leading-relaxed">
                        {language === 'es'
                          ? 'El transporte solo está incluido dentro del área del Volcán Arenal. Si requieres transporte desde otra zona, comunícate con Travel Guide Costa Rica para cotizar y agendar el pickup.'
                          : 'Transport is only included within the Arenal Volcano area. If you need transport from another area, please contact Travel Guide Costa Rica to quote and schedule your pickup.'}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {tl.duration}
                    </span>
                    {!isRafting && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {t('booking.max')} {tour.max_passengers}
                      </span>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    {(isAtv || isBuggy) ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{language === 'es' ? `Precio por ${isBuggy ? 'buggy (2 pax)' : 'ATV (1 pax)'}` : `Price per ${isBuggy ? 'buggy (2 pax)' : 'ATV (1 pax)'}`}</span>
                          <span className="font-semibold">${tour.price}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{isBuggy ? 'Buggies' : 'ATVs'}</span>
                          <span className="font-semibold">{getMachineCount()} × ${tour.price} = ${tour.price * getMachineCount()}</span>
                        </div>
                        {getActivityExtraCount() > 0 && (
                          <div className="flex justify-between items-center mb-2 text-[#F08A5D]">
                            <span>{language === 'es' ? 'Pasajero extra' : 'Extra passenger'} ({getActivityExtraCount()} × ${getActivityExtraFee()})</span>
                            <span className="font-semibold">+${getActivityExtraTotal()}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center mb-2 text-gray-500 text-sm">
                          <span>{language === 'es' ? 'Total personas' : 'Total people'}</span>
                          <span>{getMachineTotalPax()}</span>
                        </div>
                      </>
                    ) : isKayakDamas ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{language === 'es' ? 'Transporte (hasta 5 pax)' : 'Transport (up to 5 pax)'}</span>
                          <span className="font-semibold">${tour.price}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.paxLabel')}</span>
                          <span className="font-semibold">{formData.passengers}</span>
                        </div>
                        {getExtraPassengers() > 0 && (
                          <div className="flex justify-between items-center mb-2 text-[#F08A5D]">
                            <span>{t('booking.extraPax')} ({getExtraPassengers()} × ${EXTRA_PASSENGER_FEE_KAYAK})</span>
                            <span className="font-semibold">+${getExtraFee()}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center mb-2 text-[#F08A5D]">
                          <span>Kayak (${KAYAK_PER_PERSON} × {formData.passengers})</span>
                          <span className="font-semibold">+${KAYAK_PER_PERSON * formData.passengers}</span>
                        </div>
                      </>
                    ) : isBijagual ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{language === 'es' ? 'Transporte (hasta 5 pax)' : 'Transport (up to 5 pax)'}</span>
                          <span className="font-semibold">${tour.price}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.paxLabel')}</span>
                          <span className="font-semibold">{formData.passengers}</span>
                        </div>
                        {Math.max(0, formData.passengers - 5) > 0 && (
                          <div className="flex justify-between items-center mb-2 text-[#F08A5D]">
                            <span>{language === 'es' ? 'Pax extra' : 'Extra pax'} ({Math.max(0, formData.passengers - 5)} × ${BIJAGUAL_EXTRA_PAX})</span>
                            <span className="font-semibold">+${Math.max(0, formData.passengers - 5) * BIJAGUAL_EXTRA_PAX}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center mb-2 text-[#F08A5D]">
                          <span>{language === 'es' ? 'Por persona' : 'Per person'} (${BIJAGUAL_PARK_ENTRY} × {formData.passengers})</span>
                          <span className="font-semibold">+${BIJAGUAL_PARK_ENTRY * formData.passengers}</span>
                        </div>
                      </>
                    ) : isPerPerson ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.pricePerPerson') || 'Price per person'}</span>
                          <span className="font-semibold">${tour.price}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.paxLabel')}</span>
                          <span className="font-semibold">{formData.passengers}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2 text-[#F08A5D]">
                          <span>${tour.price} × {formData.passengers}</span>
                          <span className="font-semibold">${tour.price * formData.passengers}</span>
                        </div>
                      </>
                    ) : isFlatPrice && isGimmeFlatVat ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">
                            {language === 'es' ? 'Precio por máquina' : 'Price per vehicle'}
                          </span>
                          <span className="font-semibold">${tour.price}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">
                            {language === 'es' ? 'Máquinas' : 'Vehicles'}
                            <span className="text-xs text-gray-400 ml-1">× ${tour.price}</span>
                          </span>
                          <span className="font-semibold">{formData.machines || 1}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.paxLabel')}</span>
                          <span className="font-semibold">{formData.passengers}</span>
                        </div>
                      </>
                    ) : isFlatPrice ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">
                            {language === 'es'
                              ? `1-${tour?.max_passengers || 5} pax`
                              : `1-${tour?.max_passengers || 5} pax`}
                          </span>
                          <span className="font-semibold">${tour.price}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.paxLabel')}</span>
                          <span className="font-semibold">{formData.passengers}</span>
                        </div>
                      </>
                    ) : isTransfer ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.paxRange')}</span>
                          <span className="font-semibold">${tour.price}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2 text-gray-500 text-sm">
                          <span>{t('booking.extraPax')}</span>
                          <span>${getExtraPassengerFee()}{t('booking.extraPaxFee')}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.paxLabel')}</span>
                          <span className="font-semibold">{formData.passengers}</span>
                        </div>
                        {getExtraPassengers() > 0 && (
                          <div className="flex justify-between items-center mb-2 text-[#F08A5D]">
                            <span>{t('booking.extraPax')} ({getExtraPassengers()} × ${getExtraPassengerFee()})</span>
                            <span className="font-semibold">+${getExtraFee()}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.basePrice')}</span>
                          <span className="font-semibold">${tour.price}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t('booking.paxLabel')}</span>
                          <span className="font-semibold">{formData.passengers}</span>
                        </div>
                        {getExtraFee() > 0 && (
                          <div className="flex justify-between items-center mb-2 text-[#F08A5D]">
                            <span>{t('booking.extraPax')} ({getExtraPassengers()} × ${getExtraPassengerFee()})</span>
                            <span className="font-semibold">+${getExtraFee()}</span>
                          </div>
                        )}
                      </>
                    )}
                    {getDiscountAmount() > 0 && (
                      <div className="flex justify-between items-center mb-2 text-emerald-600" data-testid="booking-discount-row">
                        <span className="flex items-center gap-1">
                          🌿 {language === 'es' ? (tour.discount_label_es || 'Temporada Verde') : (tour.discount_label || 'Green Season')} (-{discountPercent}%)
                        </span>
                        <span className="font-semibold">-${getDiscountAmount().toFixed(2)}</span>
                      </div>
                    )}
                    {getAddOnsTotal() > 0 && (
                      <div className="flex justify-between items-center mb-2 text-[#1D4E41]">
                        <span>{t('booking.addOns')}</span>
                        <span className="font-semibold">+${getAddOnsTotal().toFixed(2)}</span>
                      </div>
                    )}
                    {isManuelAntonio && (
                      <div className="flex justify-between items-center mb-2 text-gray-500 text-sm">
                        <span>{t('booking.nationalParkFees')}</span>
                        <span className="italic">{t('booking.bookOnline')}</span>
                      </div>
                    )}
                    {isVatIncluded ? (
                      <>
                        {getAddonVatExtra() > 0 && (
                          <div className="flex justify-between items-center mb-2 text-gray-600 text-sm" data-testid="addon-vat-row">
                            <span>{language === 'es' ? 'IVA 13% (sobre extras)' : 'VAT 13% (on extras)'}</span>
                            <span className="font-semibold">+${getAddonVatExtra().toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                          <span className="font-semibold text-lg">{t('booking.total')}</span>
                          <span className="font-bold text-2xl text-[#1D4E41]">${getTotalPrice().toFixed(2)}</span>
                        </div>
                        <div className="text-center text-sm text-gray-500 mt-2">
                          {getAddonVatExtra() > 0
                            ? (language === 'es' ? 'IVA incluido en tour base; IVA 13% se suma sobre extras opcionales' : 'VAT included in base tour; 13% VAT added on optional extras')
                            : t('booking.vatIncluded')}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-2 pt-2 border-t border-gray-100">
                          <span className="text-gray-600">{t('booking.subtotal')}</span>
                          <span className="font-semibold">${getSubtotal().toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2 text-gray-600">
                          <span>{t('booking.vat')}</span>
                          <span className="font-semibold">+${getVatAmount().toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                          <span className="font-semibold text-lg">{t('booking.total')}</span>
                          <span className="font-bold text-2xl text-[#1D4E41]">${getTotalPrice().toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div className="lg:col-span-2">
              {/* Progress Steps */}
              <div className="flex items-center gap-4 mb-8">
                <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#1D4E41]' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#1D4E41] text-white' : 'bg-gray-200'}`}>
                    {step > 1 ? <Check className="w-4 h-4" /> : '1'}
                  </div>
                  <span className="font-medium">{t('booking.step1')}</span>
                </div>
                <div className="flex-1 h-px bg-gray-200"></div>
                <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#1D4E41]' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#1D4E41] text-white' : 'bg-gray-200'}`}>
                    2
                  </div>
                  <span className="font-medium">{t('booking.step2')}</span>
                </div>
              </div>

              {step === 1 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-6">{t('booking.bookingDetails')}</h3>
                  
                  <form onSubmit={handleCreateBooking} className="space-y-6" data-testid="booking-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('booking.fullName')} *</label>
                        <input
                          type="text"
                          name="customer_name"
                          required
                          value={formData.customer_name}
                          onChange={handleInputChange}
                          data-testid="input-name"
                          className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all"
                          placeholder={t('booking.fullNamePlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('booking.email')} *</label>
                        <input
                          type="email"
                          name="customer_email"
                          required
                          value={formData.customer_email}
                          onChange={handleInputChange}
                          data-testid="input-email"
                          className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all"
                          placeholder={t('booking.emailPlaceholder')}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('booking.phone')} *</label>
                        <input
                          type="tel"
                          name="customer_phone"
                          required
                          value={formData.customer_phone}
                          onChange={handleInputChange}
                          data-testid="input-phone"
                          className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all"
                          placeholder={t('booking.phonePlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('booking.date')} *</label>
                        <div className="relative">
                          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="date"
                            name="date"
                            required
                            min={getMinDate()}
                            value={formData.date}
                            onChange={handleInputChange}
                            data-testid="input-date"
                            className="w-full h-12 pl-12 pr-4 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    {Array.isArray(tour?.time_slots) && tour.time_slots.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {language === 'es' ? 'Hora de inicio' : 'Start time'} *
                        </label>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots-grid">
                          {tour.time_slots.map((slot) => {
                            const active = formData.time_slot === slot;
                            return (
                              <button
                                key={slot}
                                type="button"
                                data-testid={`time-slot-${slot.replace(/[: ]/g, '-')}`}
                                onClick={() => setFormData(prev => ({ ...prev, time_slot: slot }))}
                                className={`h-11 px-3 rounded-lg border text-sm font-medium transition-all active:scale-95 ${
                                  active
                                    ? 'bg-[#1D4E41] border-[#1D4E41] text-white shadow-sm'
                                    : 'bg-white border-gray-200 text-gray-700 hover:border-[#1D4E41] hover:bg-emerald-50'
                                }`}
                              >
                                {slot}
                              </button>
                            );
                          })}
                        </div>
                        {!formData.time_slot && (
                          <p className="text-xs text-gray-500 mt-2">
                            {language === 'es' ? 'Selecciona el horario que prefieras' : 'Pick the time slot that works for you'}
                          </p>
                        )}
                      </div>
                    )}

                    {(isAtv || isBuggy) ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {language === 'es' ? (isBuggy ? 'Tus Buggies' : 'Tus ATVs') : (isBuggy ? 'Your Buggies' : 'Your ATVs')} *
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="machine-cards-grid">
                          {(formData.machine_flags || []).slice(0, getMachineCount()).map((extras, idx) => {
                            const extrasInt = Math.max(0, Math.min(parseInt(extras) || 0, getMaxExtrasPerMachine()));
                            const hasExtras = extrasInt > 0;
                            const totalPaxOnMachine = getMachineBaseSeats() + extrasInt;
                            const machinePrice = tour.price + extrasInt * getMachineExtraFee();
                            const setExtras = (newExtras) => {
                              const clamped = Math.max(0, Math.min(newExtras, getMaxExtrasPerMachine()));
                              setFormData(prev => {
                                const flags = [...(prev.machine_flags || [])];
                                flags[idx] = clamped;
                                const sp = flags.reduce((acc, v) => acc + (parseInt(v) || 0), 0);
                                const m = flags.length;
                                return { ...prev, machine_flags: flags, second_passengers: sp, passengers: m * getMachineBaseSeats() + sp };
                              });
                            };
                            return (
                            <div
                              key={idx}
                              data-testid={`machine-card-${idx}`}
                              className={`relative rounded-xl border-2 p-4 transition-all ${hasExtras ? 'border-[#F08A5D] bg-orange-50' : 'border-gray-200 bg-white hover:border-[#1D4E41]'}`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${isBuggy ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                                    {isBuggy ? '🚙' : '🏎️'}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-[#1A1A1A]">
                                      {isBuggy ? (language === 'es' ? 'Buggy' : 'Buggy') : 'ATV'} #{idx + 1}
                                    </p>
                                    <p className="text-xs text-gray-500">${machinePrice} · {totalPaxOnMachine} pax</p>
                                  </div>
                                </div>
                              </div>
                              {getMaxExtrasPerMachine() === 1 ? (
                              <button
                                type="button"
                                data-testid={`machine-toggle-2nd-${idx}`}
                                onClick={() => setExtras(extrasInt === 0 ? 1 : 0)}
                                className={`w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${hasExtras ? 'bg-[#F08A5D] text-white hover:bg-[#e07a4d]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                              >
                                <UserPlus className="w-4 h-4" />
                                {hasExtras
                                  ? (language === 'es' ? `2do pasajero (+$${getMachineExtraFee()})` : `2nd passenger (+$${getMachineExtraFee()})`)
                                  : (language === 'es' ? `Agregar 2do pasajero +$${getMachineExtraFee()}` : `Add 2nd passenger +$${getMachineExtraFee()}`)}
                              </button>
                              ) : (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600 mb-1">
                                  {language === 'es' ? `Pasajeros extra (2 base incl, +$${getMachineExtraFee()} c/u)` : `Extra passengers (2 base incl, +$${getMachineExtraFee()} each)`}
                                </p>
                                <div className="flex items-center justify-between gap-2">
                                  <button type="button" data-testid={`machine-extras-minus-${idx}`} disabled={extrasInt <= 0} onClick={() => setExtras(extrasInt - 1)} className="w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 flex items-center justify-center transition-all"><Minus className="w-4 h-4" /></button>
                                  <div className="flex-1 text-center" data-testid={`machine-extras-count-${idx}`}><span className="font-bold text-lg">{extrasInt}</span><span className="text-xs text-gray-500 ml-1">/ {getMaxExtrasPerMachine()}</span></div>
                                  <button type="button" data-testid={`machine-extras-plus-${idx}`} disabled={extrasInt >= getMaxExtrasPerMachine()} onClick={() => setExtras(extrasInt + 1)} className="w-9 h-9 rounded-lg bg-[#F08A5D] text-white hover:bg-[#e07a4d] disabled:opacity-40 flex items-center justify-center transition-all"><Plus className="w-4 h-4" /></button>
                                </div>
                              </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between mt-4 gap-3">
                          <button
                            type="button"
                            data-testid="machine-remove-btn"
                            disabled={getMachineCount() <= 1}
                            onClick={() => {
                              setFormData(prev => {
                                if ((prev.machines || 1) <= 1) return prev;
                                const flags = [...(prev.machine_flags || [])];
                                flags.pop();
                                const m = flags.length;
                                const sp = flags.reduce((acc, v) => acc + (parseInt(v) || 0), 0);
                                return { ...prev, machines: m, machine_flags: flags, second_passengers: sp, passengers: m * getMachineBaseSeats() + sp };
                              });
                            }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          >
                            <Minus className="w-4 h-4" />
                            {language === 'es' ? 'Quitar' : 'Remove'}
                          </button>
                          <div className="text-center flex-1 text-sm">
                            <p className="font-semibold text-[#1D4E41]" data-testid="machine-count-display">
                              {getMachineCount()} {isBuggy ? (getMachineCount() === 1 ? 'buggy' : 'buggies') : (getMachineCount() === 1 ? 'ATV' : 'ATVs')}
                            </p>
                            <p className="text-xs text-gray-500" data-testid="total-people-display">
                              {language === 'es' ? 'Total personas:' : 'Total people:'} {getMachineTotalPax()}
                            </p>
                          </div>
                          <button
                            type="button"
                            data-testid="machine-add-btn"
                            disabled={getMachineCount() >= getMaxMachines()}
                            onClick={() => {
                              setFormData(prev => {
                                if ((prev.machines || 1) >= getMaxMachines()) return prev;
                                const flags = [...(prev.machine_flags || []), 0];
                                const m = flags.length;
                                const sp = flags.reduce((acc, v) => acc + (parseInt(v) || 0), 0);
                                return { ...prev, machines: m, machine_flags: flags, second_passengers: sp, passengers: m * getMachineBaseSeats() + sp };
                              });
                            }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1D4E41] text-white hover:bg-[#163b30] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          >
                            <Plus className="w-4 h-4" />
                            {language === 'es' ? `Agregar ${isBuggy ? 'buggy' : 'ATV'}` : `Add ${isBuggy ? 'buggy' : 'ATV'}`}
                          </button>
                        </div>
                        {/* Hidden inputs preserved for backend compatibility/tests */}
                        <input type="hidden" data-testid="input-machines" name="machines" value={getMachineCount()} readOnly />
                        <input type="hidden" data-testid="input-second-passengers" name="second_passengers" value={getSecondPaxCount()} readOnly />
                        <p className="text-xs text-gray-500 mt-3">
                          {isBuggy
                            ? (language === 'es'
                                ? `$${tour.price} por buggy (2 pax) · +$${getMachineExtraFee()} por pasajero extra · máx 4 pax por buggy · IVA 13% se suma al final`
                                : `$${tour.price} per buggy (2 pax) · +$${getMachineExtraFee()} per extra passenger · max 4 pax per buggy · 13% VAT added at end`)
                            : (language === 'es'
                                ? `$${tour.price} por ATV (1 pax) · +$${getMachineExtraFee()} por 2do pasajero · máx 2 pax por ATV · IVA 13% se suma al final`
                                : `$${tour.price} per ATV (1 pax) · +$${getMachineExtraFee()} per 2nd passenger · max 2 pax per ATV · 13% VAT added at end`)}
                        </p>
                      </div>
                    ) : (
                    <div>
                      {isGimmeFlatVat && (
                        <div className="mb-4 p-4 bg-emerald-50/60 border border-emerald-100 rounded-xl">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {language === 'es' ? 'Número de máquinas' : 'Number of vehicles'}
                          </label>
                          <div className="flex items-center justify-between gap-2">
                            <button
                              type="button"
                              data-testid="gimme-machine-minus"
                              disabled={(formData.machines || 1) <= 1}
                              onClick={() => {
                                const PPM = { 'tour-combo-gimme-all-double': 2, 'tour-combo-gimme-all-atv-double': 2, 'tour-combo-gimme-all-buggy-3pax': 3, 'tour-combo-gimme-all-buggy-4pax': 4 };
                                const ppm = tour?.pax_per_machine || PPM[tourId] || 2;
                                setFormData(prev => {
                                  const m = Math.max(1, (prev.machines || 1) - 1);
                                  return { ...prev, machines: m, passengers: m * ppm };
                                });
                              }}
                              className="w-11 h-11 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 flex items-center justify-center transition-all"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <div className="flex-1 text-center" data-testid="gimme-machine-count">
                              <span className="font-bold text-2xl text-[#1D4E41]">{formData.machines || 1}</span>
                              <span className="text-xs text-gray-500 ml-2">
                                × ${tour.price} = <span className="font-semibold text-gray-900">${(tour.price * (formData.machines || 1)).toFixed(0)}</span>
                              </span>
                            </div>
                            <button
                              type="button"
                              data-testid="gimme-machine-plus"
                              disabled={((formData.machines || 1) + 1) * (tour?.pax_per_machine || 2) > (tour?.max_passengers || 16)}
                              onClick={() => {
                                const PPM = { 'tour-combo-gimme-all-double': 2, 'tour-combo-gimme-all-atv-double': 2, 'tour-combo-gimme-all-buggy-3pax': 3, 'tour-combo-gimme-all-buggy-4pax': 4 };
                                const ppm = tour?.pax_per_machine || PPM[tourId] || 2;
                                const maxMachines = Math.floor((tour?.max_passengers || 16) / ppm);
                                setFormData(prev => {
                                  const m = Math.min(maxMachines, (prev.machines || 1) + 1);
                                  return { ...prev, machines: m, passengers: m * ppm };
                                });
                              }}
                              className="w-11 h-11 rounded-lg bg-[#1D4E41] text-white hover:bg-[#163b30] disabled:opacity-40 flex items-center justify-center transition-all"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-2 text-center">
                            {language === 'es'
                              ? `Cada máquina lleva ${tour?.pax_per_machine || 2} personas. Total: ${(formData.machines || 1) * (tour?.pax_per_machine || 2)} pasajero${(formData.machines || 1) * (tour?.pax_per_machine || 2) === 1 ? '' : 's'}.`
                              : `Each vehicle carries ${tour?.pax_per_machine || 2} people. Total: ${(formData.machines || 1) * (tour?.pax_per_machine || 2)} passenger${(formData.machines || 1) * (tour?.pax_per_machine || 2) === 1 ? '' : 's'}.`}
                          </p>
                        </div>
                      )}
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('booking.passengers')} *</label>
                      <div className="relative">
                        <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <select
                          name="passengers"
                          required
                          disabled={isGimmeFlatVat}
                          value={formData.passengers}
                          onChange={handleInputChange}
                          data-testid="input-passengers"
                          className="w-full h-12 pl-12 pr-4 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all appearance-none disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                          {[...Array(maxPax)].map((_, i) => {
                            const pax = i + 1;
                            const minRequired = tour?.min_passengers || 1;
                            if (pax < minRequired) return null;
                            // Per-person tours: show running total
                            if (isPerPerson) {
                              return (
                                <option key={pax} value={pax}>
                                  {pax} {pax === 1 ? t('booking.passenger') : t('booking.passengersPlural')} (${tour.price * pax})
                                </option>
                              );
                            }
                            // No extra fee for flat price tours
                            if (isFlatPrice) {
                              return (
                                <option key={pax} value={pax}>
                                  {pax} {pax === 1 ? t('booking.passenger') : t('booking.passengersPlural')}
                                </option>
                              );
                            }
                            const extraFee = isTransfer ? EXTRA_PASSENGER_FEE_TRANSFER : (isKayakDamas ? EXTRA_PASSENGER_FEE_KAYAK : EXTRA_PASSENGER_FEE);
                            const extraTransport = pax > BASE_PASSENGERS ? (pax - BASE_PASSENGERS) * extraFee : 0;
                            return (
                              <option key={pax} value={pax}>
                                {pax} {pax === 1 ? t('booking.passenger') : t('booking.passengersPlural')} {extraTransport > 0 ? `(+$${extraTransport})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      {isFlatPrice && isGimmeFlatVat ? (
                        <p className="text-xs text-gray-500 mt-1">
                          {language === 'es'
                            ? `Auto-calculado según # de máquinas (arriba)`
                            : `Auto-set by # of vehicles above`}
                        </p>
                      ) : isFlatPrice ? (
                        <p className="text-xs text-gray-500 mt-1">
                          {language === 'es'
                            ? `Tarifa fija para hasta ${tour?.max_passengers || 5} pasajero${(tour?.max_passengers || 5) === 1 ? '' : 's'}`
                            : `Flat rate for up to ${tour?.max_passengers || 5} passenger${(tour?.max_passengers || 5) === 1 ? '' : 's'}`}
                        </p>
                      ) : isPerPerson ? (
                        <p className="text-xs text-gray-500 mt-1">
                          ${tour.price} {t('booking.perPersonNote') || 'per person'}
                          {tour?.min_passengers && tour.min_passengers > 1 ? (
                            <span className="ml-1 text-amber-700">
                              {language === 'es' ? `· mín. ${tour.min_passengers} personas` : `· min. ${tour.min_passengers} guests`}
                            </span>
                          ) : null}
                        </p>
                      ) : isBijagual ? (
                        <p className="text-xs text-gray-500 mt-1">
                          {language === 'es'
                            ? `Transporte $${tour.price} (hasta 5 pax) + $${BIJAGUAL_EXTRA_PAX}/pax extra + $${BIJAGUAL_PARK_ENTRY}/persona`
                            : `Transport $${tour.price} (up to 5 pax) + $${BIJAGUAL_EXTRA_PAX}/extra pax + $${BIJAGUAL_PARK_ENTRY}/person`}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">{t('booking.upToIncluded')} +${getExtraPassengerFee()}{t('booking.perExtra')}</p>
                      )}
                    </div>
                    )}

                    {/* Add-ons Section */}
                    {hasAddOns && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                        <h4 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                          <span className="text-2xl">✨</span> {t('booking.optionalAddOns')}
                        </h4>
                        <div className="space-y-4">
                          {tour.add_ons.map((addon) => (
                            <div key={addon.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-lg p-4 border border-amber-100">
                              <div className="flex-1">
                                <h5 className="font-medium text-[#1A1A1A]">{addon.name}</h5>
                                <p className="text-sm text-gray-500">{addon.description}</p>
                                <p className="text-sm font-semibold text-[#1D4E41]">+${addon.price_per_person}{t('booking.perPerson')}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <label className="text-sm text-gray-600">{t('booking.howMany')}</label>
                                <select
                                  value={selectedAddOns[addon.id] || 0}
                                  onChange={(e) => handleAddOnChange(addon.id, parseInt(e.target.value))}
                                  data-testid={`addon-qty-${addon.id}`}
                                  className="w-20 h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none"
                                >
                                  <option value={0}>0</option>
                                  {[...Array(formData.passengers)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                        {getAddOnsTotal() > 0 && (
                          <div className="mt-4 pt-4 border-t border-amber-200 text-right">
                            <span className="text-sm text-gray-600">{t('booking.addOnsTotal')} </span>
                            <span className="font-semibold text-[#1D4E41]">+${getAddOnsTotal().toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('booking.notes')}</label>
                      <textarea
                        name="notes"
                        rows={3}
                        value={formData.notes}
                        onChange={handleInputChange}
                        data-testid="input-notes"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all resize-none"
                        placeholder={t('booking.notesPlaceholder')}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="continue-to-payment"
                      className="w-full h-14 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('booking.processing')}
                        </>
                      ) : (
                        <>
                          {t('booking.continuePayment')}
                          <CreditCard className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}

              {step === 2 && booking && (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-2">{t('booking.selectPaymentMethod')}</h3>
                  <p className="text-gray-600 mb-8">{t('booking.paymentSubtitle')}</p>
                  
                  <div className="space-y-4">
                    {/* Stripe Payment */}
                    <button
                      onClick={() => handlePayment('stripe')}
                      disabled={submitting}
                      data-testid="pay-with-stripe"
                      className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-[#1D4E41] transition-all flex items-center justify-between group disabled:opacity-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#635BFF] rounded-lg flex items-center justify-center">
                          <CreditCard className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-[#1A1A1A]">{t('booking.payWithStripe')}</div>
                          <div className="text-sm text-gray-500">{t('booking.stripeDesc')}</div>
                        </div>
                      </div>
                      <div className="text-[#1D4E41] font-semibold group-hover:translate-x-1 transition-transform">
                        ${Number(getTotalPrice()).toFixed(2)} →
                      </div>
                    </button>

                    {/* PayPal Payment */}
                    <button
                      onClick={() => handlePayment('paypal')}
                      disabled={submitting}
                      data-testid="pay-with-paypal"
                      className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-[#1D4E41] transition-all flex items-center justify-between group disabled:opacity-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#003087] rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-sm">PP</span>
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-[#1A1A1A]">{t('booking.payWithPaypal')}</div>
                          <div className="text-sm text-gray-500">{t('booking.paypalDesc')}</div>
                        </div>
                      </div>
                      <div className="text-[#1D4E41] font-semibold group-hover:translate-x-1 transition-transform">
                        ${Number(getTotalPrice()).toFixed(2)} →
                      </div>
                    </button>
                  </div>

                  <div className="mt-8 p-4 bg-[#E8F5E9] rounded-xl flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-[#1D4E41] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[#1D4E41]">
                      <strong>{t('booking.securePayment')}</strong> {t('booking.secureDesc')}
                    </div>
                  </div>

                  <button
                    onClick={() => setStep(1)}
                    data-testid="back-to-details"
                    className="mt-6 text-gray-600 hover:text-[#1D4E41] font-medium flex items-center gap-2 mx-auto"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t('booking.backToDetails')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookingPage;
